import React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@pw/batch-widgets'
import { Button, Typography } from '@pw-tech/omni-ui'
import cn from 'clsx'
import style from './index.module.css'
import InfinityCard from '@components/InfinityCard'
import {
  BatchPlanProps,
  InfinitySectionWrapperProps,
} from '../../../utils/types'
import { getDiscountedPrice, useDeviceSize } from '../../../utils/utilities'
import { PlanVariant } from '../../../utils/enum'

const InfinitySectionWrapper: React.FC<InfinitySectionWrapperProps> = ({
  title,
  batchPlans,
  videoTitle,
  selectedPlan,
  planClicked,
  handlePlanSelect,
}) => {
  const [width] = useDeviceSize()
  const isSinglePlan = batchPlans?.length === 1
  const wrapperClass = cn({
    [style.singlePlanWrapper]: isSinglePlan,
    [style.wrapper]: !isSinglePlan,
  })

  const gridWrapperClass = cn({
    [style.gridWrapper]: !isSinglePlan,
  })

  const getCardVariant = (index: number) => {
    const variants = [
      PlanVariant.BASIC,
      PlanVariant.INFINITY,
      PlanVariant.INFINITY_PRO,
    ]
    return variants[index] || PlanVariant.INFINITY_PRO
  }

  const selectedPlanIndex = batchPlans?.findIndex(
    (plan) => plan?.planId === selectedPlan?.planId
  )

  const renderInfinityCard = (plan: BatchPlanProps, index: number) => {
    return (
      <InfinityCard
        key={index}
        index={index}
        variant={getCardVariant(index)}
        data={plan.tableItems}
        price={plan.price}
        total={getDiscountedPrice(plan.total, plan.discount)}
        discount={plan.discount}
        planName={plan.title}
        planId={plan.planId}
        planVideoId={plan.planVideoId}
        isRecommended={plan.isRecommended}
        selectedPlan={selectedPlan}
        isSinglePlan={isSinglePlan}
        handlePlanSelect={handlePlanSelect}
        isProPlan={batchPlans.length >= 3}
        planBadges={plan?.planBadges}
        courseId={plan?.courseId}
        planClicked={planClicked}
      />
    )
  }
  const renderPlans = () => batchPlans?.map(renderInfinityCard)

  const renderGrid = () => (
    <div className={gridWrapperClass}>{renderPlans()}</div>
  )

  const renderCarousel = () => (
    <>
      {width > 640 && (
        <div className="relative h-auto">
          <Carousel
            opts={{
              align: 'start',
              loop: false,
              startIndex: 0,
            }}
            className="relative w-full"
          >
            <CarouselContent>
              {renderPlans().map((card, index) => (
                <CarouselItem
                  key={index}
                  className="z-10 flex basis-[43%] gap-4 mb-4"
                >
                  {card}
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious
              className={'left-2 z-10 ml-6 bg-white hover:bg-white'}
            >
              <ChevronLeft size={30} />
            </CarouselPrevious>
            <CarouselNext
              className={'right-2 z-10 mr-6 bg-white hover:bg-white'}
            >
              <ChevronRight size={30} />
            </CarouselNext>
          </Carousel>
        </div>
      )}
      {width <= 640 && <div className="block md:hidden">{renderGrid()}</div>}
    </>
  )

  return (
    <div className={wrapperClass}>
      {title && (
        <Typography
          component="h2"
          color="static-white"
          variant="heading2"
          weight="bold"
          className={style.title}
        >
          {title}
        </Typography>
      )}

      {!isSinglePlan ? (
        <>{batchPlans.length >= 3 ? renderCarousel() : renderGrid()}</>
      ) : (
        renderPlans()
      )}

      {videoTitle && isSinglePlan && selectedPlanIndex >= 0 && (
        <div className={style.singlePlan} />
      )}
    </div>
  )
}

export default InfinitySectionWrapper
